import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const CSS_HSL = () => {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">🌈 CSS HSL Colors</h1>

      <p className="tutorial-intro">
        HSL stands for <strong>Hue</strong>, <strong>Saturation</strong>, and{" "}
        <strong>Lightness</strong>. It provides a more natural way to describe
        and adjust colors compared to RGB or HEX.
      </p>

      {/* Syntax */}
      <div className="tutorial-section">
        <h2 className="section-title">1️⃣ Syntax</h2>
        <CodeCopyBlock code={`color: hsl(hue, saturation, lightness);`} />
        <p>
          - <strong>Hue</strong>: a degree on the color wheel (0–360). <br />-{" "}
          <strong>Saturation</strong>: % of color intensity (0% = gray, 100% =
          full color). <br />- <strong>Lightness</strong>: % of brightness (0% =
          black, 50% = normal, 100% = white).
        </p>
      </div>

      {/* Hue */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ Hue (0–360)</h2>
        <p>
          Hue defines the type of color. Example: 0 = red, 120 = green, 240 =
          blue.
        </p>
        <CodeCopyBlock
          code={`h1 { color: hsl(0, 100%, 50%); }   /* Red */
p  { color: hsl(120, 100%, 50%); } /* Green */
div { color: hsl(240, 100%, 50%); } /* Blue */`}
        />

        <div style={{ display: "flex", gap: "1rem", marginTop: "1rem" }}>
          <div
            style={{
              backgroundColor: "hsl(0,100%,50%)",
              color: "white",
              padding: "1rem",
              borderRadius: "8px",
              flex: 1,
              textAlign: "center",
            }}
          >
            hsl(0,100%,50%) → Red
          </div>
          <div
            style={{
              backgroundColor: "hsl(120,100%,50%)",
              color: "black",
              padding: "1rem",
              borderRadius: "8px",
              flex: 1,
              textAlign: "center",
            }}
          >
            hsl(120,100%,50%) → Green
          </div>
          <div
            style={{
              backgroundColor: "hsl(240,100%,50%)",
              color: "white",
              padding: "1rem",
              borderRadius: "8px",
              flex: 1,
              textAlign: "center",
            }}
          >
            hsl(240,100%,50%) → Blue
          </div>
        </div>
      </div>

      {/* Saturation */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ Saturation (0%–100%)</h2>
        <p>
          Saturation controls the intensity of the color. At 0%, the color looks
          gray. At 100%, it’s the most vivid.
        </p>
        <CodeCopyBlock
          code={`div { color: hsl(200, 100%, 50%); } /* Fully saturated */
div { color: hsl(200, 50%, 50%); }  /* Less saturated */
div { color: hsl(200, 0%, 50%); }   /* Gray */`}
        />

        <div
          style={{
            display: "flex",
            gap: "1rem",
            marginTop: "1rem",
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              backgroundColor: "hsl(200,100%,50%)",
              color: "white",
              padding: "1rem",
              borderRadius: "8px",
              flex: 1,
              textAlign: "center",
            }}
          >
            100% Saturation
          </div>
          <div
            style={{
              backgroundColor: "hsl(200,50%,50%)",
              color: "white",
              padding: "1rem",
              borderRadius: "8px",
              flex: 1,
              textAlign: "center",
            }}
          >
            50% Saturation
          </div>
          <div
            style={{
              backgroundColor: "hsl(200,0%,50%)",
              color: "black",
              padding: "1rem",
              borderRadius: "8px",
              flex: 1,
              textAlign: "center",
            }}
          >
            0% Saturation (Gray)
          </div>
        </div>
      </div>

      {/* Lightness */}
      <div className="tutorial-section">
        <h2 className="section-title">4️⃣ Lightness (0%–100%)</h2>
        <p>
          Lightness controls how bright or dark a color appears. At 0% the color
          is black, 50% is normal, and 100% is white.
        </p>
        <CodeCopyBlock
          code={`div { color: hsl(200, 100%, 0%); }   /* Black */
div { color: hsl(200, 100%, 50%); }  /* Normal Blue */
div { color: hsl(200, 100%, 100%); } /* White */`}
        />

        <div
          style={{
            display: "flex",
            gap: "1rem",
            marginTop: "1rem",
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              backgroundColor: "hsl(200,100%,0%)",
              color: "white",
              padding: "1rem",
              borderRadius: "8px",
              flex: 1,
              textAlign: "center",
            }}
          >
            0% Lightness (Black)
          </div>
          <div
            style={{
              backgroundColor: "hsl(200,100%,50%)",
              color: "white",
              padding: "1rem",
              borderRadius: "8px",
              flex: 1,
              textAlign: "center",
            }}
          >
            50% Lightness (Normal Blue)
          </div>
          <div
            style={{
              backgroundColor: "hsl(200,100%,100%)",
              color: "black",
              padding: "1rem",
              borderRadius: "8px",
              flex: 1,
              textAlign: "center",
            }}
          >
            100% Lightness (White)
          </div>
        </div>
      </div>

      {/* HSLA */}
      <div className="tutorial-section">
        <h2 className="section-title">5️⃣ HSLA (with Alpha)</h2>
        <p>
          Just like RGBA and HEX8, HSL also supports transparency using an{" "}
          <code>alpha</code> value.
        </p>
        <CodeCopyBlock
          code={`div { background-color: hsla(200, 100%, 50%, 0.5); }`}
        />

        <div
          style={{
            position: "relative",
            background: "#eee",
            padding: "2rem",
            borderRadius: "8px",
            marginTop: "1rem",
          }}
        >
          <div
            style={{
              backgroundColor: "hsla(200,100%,50%,0.5)",
              padding: "1rem",
              borderRadius: "6px",
              color: "black",
              fontWeight: "bold",
            }}
          >
            hsla(200,100%,50%,0.5) → Semi-transparent Blue
          </div>
        </div>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">🚀 Next Steps</h2>
        <p>
          HSL is great for creating consistent color themes and adjusting tints
          and shades. Next, check out{" "}
          <a href="/css/backgrounds" className="learn-more">
            CSS Backgrounds
          </a>{" "}
          to see how colors are applied in real design.
        </p>
      </div>
    </div>
  );
};

export default CSS_HSL;
